import { Close } from '@mui/icons-material'
import { Button, Grid, IconButton } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { formatSize } from 'utils/helpers'

const ALLOWED_EXTENSIONS = ['txt', 'pdf', 'rtf', 'doc', 'docx']
const MAX_FILE_SIZE = 10 * 1024 * 1024
const MAX_FILE_COUNT = 5

const GrantFitFileUpload = ({ uploadState: { selectedFiles, errors }, setUploadState }) => {
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  const setSelectedFiles = files =>
    setUploadState({
      selectedFiles: [...files],
      errors: [
        ...new Set(files.reduce((arr, file) => [...arr, ...file.errors], [])),
        ...(files.length > MAX_FILE_COUNT ? ['tooManyFiles'] : [])
      ]
    })

  const validateFile = file => {
    const errors = []
    if (!ALLOWED_EXTENSIONS.includes(file.name.toLowerCase().split('.').at(-1))) {
      errors.push('invalidExtension')
    }
    if (file.size === 0) {
      errors.push('fileEmpty')
    }
    if (file.size > MAX_FILE_SIZE) {
      errors.push('fileTooLarge')
    }
    return errors
  }

  const onSelectedFilesChange = event => {
    setSelectedFiles(
      [...event.target.files].map(file => ({
        file,
        name: file.name,
        size: file.size,
        errors: validateFile(file)
      }))
    )
    event.target.value = null
  }

  const removeSelectedFile = index => () => {
    if (index >= selectedFiles.length) return
    setSelectedFiles([...selectedFiles.slice(0, index), ...selectedFiles.slice(index + 1)])
  }

  const getErrorText = error => {
    const errorText = t.funder.grantFit.uploadDocumentsErrors[error]
    switch (error) {
      case 'fileTooLarge':
        return errorText.replace('{size}', formatSize(MAX_FILE_SIZE, 0))
      case 'invalidExtension':
        return errorText.replace('{types}', ALLOWED_EXTENSIONS.map(s => s.toUpperCase()).join(', '))
      case 'tooManyFiles':
        return errorText.replace('{count}', MAX_FILE_COUNT)
      default:
        return errorText
    }
  }

  return (
    <Grid className="grantfit-file-upload">
      <Grid container className="grantfit-input-row">
        <div>
          <input id="grantfit-input" type="file" multiple hidden onChange={onSelectedFilesChange} />
          <label htmlFor="grantfit-input">
            <Button component="span" variant="contained" className="upload-button">
              {t.funder.grantFit.uploadDocumentsButton}
            </Button>
          </label>
        </div>
        <Grid container className="grantfit-files">
          {selectedFiles.map((file, index) => (
            <div key={index} className={`grantfit-file ${file.errors?.length ? 'error' : ''}`}>
              <IconButton size="small" onClick={removeSelectedFile(index)}>
                <Close fontSize="inherit" />
              </IconButton>
              <span>{file.name}</span>
              <span className="filesize">{formatSize(file.size, 1)}B</span>
            </div>
          ))}
        </Grid>
      </Grid>
      <Grid className="grantfit-file-errors">
        {errors?.map(error => (
          <div>{getErrorText(error)}</div>
        ))}
      </Grid>
    </Grid>
  )
}

export default GrantFitFileUpload
